import {ContentEditableEditorPlugin, spliceTextNode} from '@frontapp/editor-html';
import {css} from 'styled-components';

import {findAncestor, isElement} from '../../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {isTopicVariableName} from '../../../../../helpers/models/applicationObjectVariableHelpers';
import {
  VariableInterpolator,
  variableRegexp,
} from '../../../../../services/variableInterpolation/variableInterpolator';
import {
  answerVariablePluginDataAttribute,
  answerVariablePluginTopicIdAttribute,
  surveyTemplateVariable,
} from '../../../../inboxes/composer/composerConstants';

export type RawVariable = string;

interface CreateAnswerVariablePluginOptions {
  getInterpolator: () => VariableInterpolator;
  onRawVariables?: (arg: ReadonlyArray<RawVariable>) => void;
  onNewRawVariable?: (variable: RawVariable) => void;
  getAdditionalVariableNames?: () => ReadonlyArray<string>;
  getSelectedTopicId?: () => number | undefined;
}
export function createAnswerVariablePlugin({
  getInterpolator,
  onNewRawVariable,
  onRawVariables,
  getAdditionalVariableNames,
  getSelectedTopicId,
}: CreateAnswerVariablePluginOptions): ContentEditableEditorPlugin {
  return {
    processText: (element, textNodeAndCursorPositions) => {
      const document = element.ownerDocument;
      if (!document) {
        return;
      }

      const interpolator = getInterpolator();
      const selectedTopicId = getSelectedTopicId && getSelectedTopicId();

      Array.from(textNodeAndCursorPositions).forEach(({node}) => {
        processVariablePlaceholders(
          node,
          document,
          interpolator,
          selectedTopicId,
          onNewRawVariable,
          getAdditionalVariableNames?.(),
        );
      });
    },
    processTree: (element) => {
      const interpolator = getInterpolator();
      processAnchors(element, interpolator);
      onRawVariables?.(findRawVariables(getVariables(element)));
    },
  };
}

function getVariables(container: Element) {
  return Array.from(container.querySelectorAll(`[${answerVariablePluginDataAttribute}]`));
}

function findRawVariables(elements: Array<Element>) {
  return (
    elements
      // Use `.textContent` because text may be nested in anchors.
      .map((node) => node.textContent || '')
      .filter(shouldAlertRawVariable)
      .filter((text) => VariableInterpolator.hasRawVariables(text))
  );
}

export function maybeSelectPillText(target: EventTarget, container: HTMLElement) {
  if (!isElement(target)) {
    return;
  }

  const document = target.ownerDocument;
  const window = document && document.defaultView;

  if (!document || !window) {
    return;
  }

  const pill = findAncestor(target, container, isPillElement);

  if (!pill) {
    return;
  }

  const selection = window.getSelection();

  if (!selection) {
    return;
  }

  const range = document.createRange();
  range.selectNode(pill);
  selection.removeAllRanges();
  selection.addRange(range);
}

export function updateAnswerVariables(
  container: HTMLElement,
  interpolator: VariableInterpolator,
  selectedTopicId: number | undefined,
): ReadonlyArray<RawVariable> {
  const elements = getVariables(container);
  elements.forEach((node) => {
    const variableName = node.getAttribute(answerVariablePluginDataAttribute);

    if (!variableName) {
      return;
    }

    const variablePlaceholder = `{{${variableName}}}`;
    const interpolatedValue = interpolator.interpolateText(variablePlaceholder);

    /* eslint no-param-reassign: 0 */
    node.textContent = interpolatedValue || variablePlaceholder;

    updateTopicVariableNode(node, variableName, selectedTopicId);
  });
  return findRawVariables(elements);
}

function processVariablePlaceholders(
  textNode: Text,
  document: Document,
  interpolator: VariableInterpolator,
  selectedTopicId: number | undefined,
  onNewRawVariable?: (variable: RawVariable) => void,
  additionalVariableNames: ReadonlyArray<string> = [],
) {
  let match;
  // eslint-disable-next-line
  while ((match = variableRegexp.exec(textNode.data))) {
    // Always search from the beginning of the textNode data as we're splicing it each time we find a match.
    variableRegexp.lastIndex = 0;

    const before = match[0];
    const variableName = match[1];
    const interpolatedValue = interpolator.interpolateText(before);
    const after = interpolatedValue || before;

    // Already decorated, just replace the variable if necessary.
    if (textNode.parentElement && isPillElement(textNode.parentElement)) {
      if (before !== after) {
        textNode.data = after;
      }
      break;
    }

    const node = document.createElement('span');
    node.setAttribute(answerVariablePluginDataAttribute, variableName);
    node.setAttribute('contenteditable', 'false');
    node.textContent = after;

    spliceTextNode(textNode, match.index, before.length, node);

    if (onNewRawVariable && before === after && shouldAlertRawVariable(before)) {
      onNewRawVariable(before);
    }

    updateTopicVariableNode(node, variableName, selectedTopicId);
  }
}

function updateTopicVariableNode(node: Element, variableName: string, selectedTopicId: number | undefined) {
  if (!isTopicVariableName(variableName)) {
    return;
  }
  if (selectedTopicId) {
    node.setAttribute(answerVariablePluginTopicIdAttribute, `${variableName}:${selectedTopicId.toString()}`);
  } else {
    node.setAttribute(answerVariablePluginTopicIdAttribute, `${variableName}:undefined`);
  }
}

function shouldAlertRawVariable(text: string) {
  return text !== surveyTemplateVariable;
}

function isPillElement(element: Element): element is HTMLSpanElement {
  return element.hasAttribute(answerVariablePluginDataAttribute);
}

function processAnchors(element: Element, interpolator: VariableInterpolator) {
  element.querySelectorAll('a').forEach((anchor) => {
    // Use the attribute instead of the href property as the latter gets normalized by the browser.
    const currentHref = anchor.getAttribute('href');

    if (!currentHref) {
      return;
    }

    const updatedHref = interpolator.interpolateText(currentHref);

    if (updatedHref === currentHref) {
      return;
    }

    anchor.setAttribute('href', updatedHref);
  });
}

export function addAnswerVariableStyles() {
  return css`
    [${answerVariablePluginDataAttribute}] {
      color: ${(p) => p.theme.greys.white};
      display: inline-block;
      background: ${(p) => p.theme.palette.teal.shade40};
      border-radius: 10px;
      padding: 1px 7px 3px;
      margin-bottom: 2px;
    }
  `;
}
